import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid-pro';
import { getColumns, Column } from '../../api/spreadsheet';
import { useSnackbar } from '../../context/SnackbarContext';
import { useSpreadsheetViewType } from '../../context/CampaignContext';
import { CampaignProspectType } from '../../api/campaigns';
import ModalHeader from './CSVUploadModal/ModalHeader';
import ModalActions from './CSVUploadModal/ModalActions';
import { UploadStep } from './CSVUploadModal/ModalActions';
import PreviewMappingStep from './CSVUploadModal/PreviewMappingStep';
import ScrapeWebsiteInputParamModalContent, {
  type ScrapeWebsiteInputParamModalContentRef
} from '../Prospect/ScrapeWebsiteInputParamModalContent';
import { AccountProspectResponse } from '../../types/prospects';
import type { SpreadsheetColumnTypeMap } from '../../constants/spreadsheetColumnTypes';
import ModalActionButtons from '../modal/ModalActionButtons';

enum ScrapeImportStep {
  SCRAPE = 'SCRAPE',
  PREVIEW_MAPPING = 'PREVIEW_MAPPING'
}

interface ScrapeWebsiteImportModalProps {
  open: boolean;
  onClose: () => void;
  prospectType: CampaignProspectType;
  onMappingComplete: (
    uploadedRows: Record<string, any>[],
    mapping: Record<string, string | null>,
    customColumnTypes?: SpreadsheetColumnTypeMap
  ) => void;
}

const ScrapeWebsiteImportModal: React.FC<ScrapeWebsiteImportModalProps> = ({
  open,
  onClose,
  prospectType,
  onMappingComplete
}) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const viewType = useSpreadsheetViewType();
  const { showSnackbar } = useSnackbar();
  const [step, setStep] = useState<ScrapeImportStep>(ScrapeImportStep.SCRAPE);
  const [scrapedData, setScrapedData] = useState<Record<string, any>[]>([]);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [columnsDefinition, setColumnsDefinition] = useState<GridColDef[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [currentMapping, setCurrentMapping] = useState<
    Record<string, string | null>
  >({});
  const [customColumnTypes, setCustomColumnTypes] =
    useState<SpreadsheetColumnTypeMap>({});
  const [existingColumnTypes, setExistingColumnTypes] = useState<
    Record<string, string>
  >({});
  const [importing, setImporting] = useState(false);
  const [scrapeCanSubmit, setScrapeCanSubmit] = useState(false);
  const [scrapeScraping, setScrapeScraping] = useState(false);
  const scrapeFormRef = useRef<ScrapeWebsiteInputParamModalContentRef>(null);

  useEffect(() => {
    if (open && campaignId) {
      const fetchColumns = async () => {
        setLoadingColumns(true);
        try {
          const response = await getColumns(campaignId, viewType);
          const columns: Column[] = (response.columns || []).sort(
            (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
          );
          const gridCols: GridColDef[] = columns.map((col) => ({
            field: col.col_name,
            headerName: col.col_name,
            width: 150,
            sortable: true,
            filterable: false,
            editable: false
          }));
          setColumnsDefinition(gridCols);
          const typesMap: Record<string, string> = {};
          columns.forEach((col) => {
            if (col.col_type) typesMap[col.col_name] = col.col_type;
          });
          setExistingColumnTypes(typesMap);
        } catch (error: any) {
          showSnackbar(
            error?.message || 'Failed to load campaign columns',
            'error'
          );
        } finally {
          setLoadingColumns(false);
        }
      };
      fetchColumns();
    }
  }, [open, campaignId, viewType, showSnackbar]);

  const resetState = () => {
    setStep(ScrapeImportStep.SCRAPE);
    setScrapedData([]);
    setDetectedColumns([]);
    setCurrentMapping({});
    setCustomColumnTypes({});
    setExistingColumnTypes({});
  };

  const handleScrapeConfirm = (
    rows: AccountProspectResponse[],
    _config?: Record<string, any>
  ) => {
    if (!rows || rows.length === 0) {
      showSnackbar('No data to map', 'error');
      return;
    }
    setScrapedData(rows);
    setDetectedColumns(Object.keys(rows[0] || {}));
    setStep(ScrapeImportStep.PREVIEW_MAPPING);
  };

  const handleMappingChange = (mapping: Record<string, string | null>) => {
    setCurrentMapping(mapping);
  };

  const handleMappingConfirmed = async () => {
    const allFields = Object.keys(currentMapping);

    const uploadedRows = scrapedData.map((row) => {
      const newRow: Record<string, any> = {};
      allFields.forEach((field) => {
        const sourceCol = currentMapping[field];
        newRow[field] = sourceCol ? (row[sourceCol] ?? '') : '';
      });
      return newRow;
    });

    setImporting(true);
    try {
      await onMappingComplete(uploadedRows, currentMapping, customColumnTypes);
      resetState();
      onClose();
    } finally {
      setImporting(false);
    }
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const handleBack = () => {
    if (step === ScrapeImportStep.PREVIEW_MAPPING) {
      setStep(ScrapeImportStep.SCRAPE);
      setScrapedData([]);
      setDetectedColumns([]);
      setCurrentMapping({});
    }
  };

  const headers = scrapedData.length > 0 ? Object.keys(scrapedData[0]) : [];
  const rawCsvRows = [
    headers,
    ...scrapedData.map((row) => headers.map((h) => String(row[h] ?? '')))
  ];

  return (
    <Dialog
      open={open}
      onClose={importing ? () => {} : handleCancel}
      maxWidth={step === ScrapeImportStep.PREVIEW_MAPPING ? 'lg' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          maxHeight: '90vh',
          position: 'relative'
        }
      }}
    >
      {importing && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.85)',
            zIndex: 10,
            borderRadius: '20px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <CircularProgress />
            <Typography color="text.secondary">Importing data...</Typography>
          </Box>
        </Box>
      )}
      <ModalHeader
        title={
          step === ScrapeImportStep.SCRAPE
            ? 'Import from Web Scraping'
            : 'Preview & Map Columns'
        }
        onClose={importing ? () => {} : handleCancel}
      />
      <DialogContent sx={{ px: 3, py: 2 }}>
        {step === ScrapeImportStep.SCRAPE && (
          <Box sx={{ mb: 2 }}>
            <ScrapeWebsiteInputParamModalContent
              ref={scrapeFormRef}
              onConfirm={handleScrapeConfirm}
              onCancel={handleCancel}
              hideCancel
              hideButtons
              onStateChange={({ canSubmit, scraping }) => {
                setScrapeCanSubmit(canSubmit);
                setScrapeScraping(scraping);
              }}
            />
          </Box>
        )}

        {step === ScrapeImportStep.PREVIEW_MAPPING && (
          <PreviewMappingStep
            rawCsvRows={rawCsvRows}
            headerRowOffset={0}
            onHeaderRowChange={() => {}}
            columnsDefinition={columnsDefinition}
            existingColumnTypes={existingColumnTypes}
            detectedColumns={detectedColumns}
            loadingColumns={loadingColumns}
            onMappingChange={handleMappingChange}
            onColumnTypesChange={setCustomColumnTypes}
          />
        )}
      </DialogContent>
      {step === ScrapeImportStep.SCRAPE ? (
        <ModalActionButtons
          asDialogActions
          secondaryLabel="Cancel"
          primaryLabel={scrapeScraping ? 'Scraping…' : 'Start Scraping'}
          onSecondary={handleCancel}
          onPrimary={() => scrapeFormRef.current?.triggerScrape()}
          primaryDisabled={!scrapeCanSubmit || scrapeScraping}
          secondaryDisabled={scrapeScraping}
        />
      ) : (
        <ModalActions
          step={UploadStep.PREVIEW_MAPPING}
          onCancel={handleCancel}
          onBack={handleBack}
          onConfirm={handleMappingConfirmed}
          importing={importing}
        />
      )}
    </Dialog>
  );
};

export default ScrapeWebsiteImportModal;
